/**
 * CRM Settings — Currency Rates
 *
 * List historical FX rates and allow manual rate entry.
 * Manual form uses <input type="date">; currency inputs are 3-char
 * uppercase ISO 4217.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useTranslations } from 'gt-react'
import { Loader2, Plus } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/settings/crm/currency-rates')({
  component: CurrencyRatesPage,
})

interface Rate {
  id: string
  fromCurrency: string
  toCurrency: string
  rate: string
  effectiveDate: Date
  source: string
  providerKey: string | null
  fetchedAt: Date
}

function todayIso(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function CurrencyRatesPage() {
  const t = useTranslations()
  const qc = useQueryClient()
  const listQ = useQuery(orpc.crm.currency.rates.list.queryOptions({ input: { limit: 100 } }))
  const rates = (listQ.data ?? []) as Rate[]

  const [fromCurrency, setFrom] = useState('')
  const [toCurrency, setTo] = useState('')
  const [rate, setRate] = useState('')
  const [effectiveDate, setDate] = useState(todayIso())

  const createMut = useMutation(
    orpc.crm.currency.rates.create.mutationOptions({
      onSuccess: () => {
        toast.success(t('crm.common.save'))
        setFrom('')
        setTo('')
        setRate('')
        setDate(todayIso())
        qc.invalidateQueries({
          queryKey: orpc.crm.currency.rates.list.queryKey({ input: { limit: 100 } }),
        })
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const canSubmit =
    fromCurrency.length === 3 && toCurrency.length === 3 && rate !== '' && effectiveDate !== ''

  return (
    <div className="container mx-auto py-6 px-4">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">{t('crm.settings.currency.rates')}</h1>
        <p className="text-muted-foreground">{t('navigation.crmSettings')}</p>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>{t('crm.settings.currency.rateSourceManual')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
            <div>
              <Label htmlFor="from">{t('crm.settings.currency.fromCurrency')}</Label>
              <Input
                id="from"
                value={fromCurrency}
                maxLength={3}
                onChange={(e) => setFrom(e.target.value.toUpperCase())}
                placeholder="USD"
                className="uppercase"
              />
            </div>
            <div>
              <Label htmlFor="to">{t('crm.settings.currency.toCurrency')}</Label>
              <Input
                id="to"
                value={toCurrency}
                maxLength={3}
                onChange={(e) => setTo(e.target.value.toUpperCase())}
                placeholder="EUR"
                className="uppercase"
              />
            </div>
            <div>
              <Label htmlFor="rate">{t('crm.settings.currency.rate')}</Label>
              <Input
                id="rate"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                placeholder="0.9234"
                inputMode="decimal"
              />
            </div>
            <div>
              <Label htmlFor="effective">{t('crm.settings.currency.effectiveDate')}</Label>
              <Input
                id="effective"
                type="date"
                value={effectiveDate}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>
            <Button
              onClick={() =>
                createMut.mutate({
                  fromCurrency,
                  toCurrency,
                  rate,
                  effectiveDate: new Date(effectiveDate),
                })
              }
              disabled={!canSubmit || createMut.isPending}
            >
              {createMut.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-1" />
                  {t('crm.common.create')}
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('crm.settings.currency.rates')}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {listQ.isLoading ? (
            <div className="p-6 space-y-2">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : rates.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">
              {t('crm.common.noRecords')}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('crm.settings.currency.fromCurrency')}</TableHead>
                  <TableHead>{t('crm.settings.currency.toCurrency')}</TableHead>
                  <TableHead>{t('crm.settings.currency.rate')}</TableHead>
                  <TableHead>{t('crm.settings.currency.effectiveDate')}</TableHead>
                  <TableHead>{t('crm.settings.currency.rateSource')}</TableHead>
                  <TableHead>{t('crm.settings.currency.provider')}</TableHead>
                  <TableHead>{t('crm.settings.currency.lastRefresh')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rates.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-mono">{r.fromCurrency}</TableCell>
                    <TableCell className="font-mono">{r.toCurrency}</TableCell>
                    <TableCell className="font-mono">{r.rate}</TableCell>
                    <TableCell>{new Date(r.effectiveDate).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Badge variant={r.source === 'api' ? 'default' : 'outline'}>{r.source}</Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{r.providerKey ?? '-'}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {new Date(r.fetchedAt).toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
